/* @jsxRuntime classic */
/** @jsx createElement */
import {
  BodyThree,
  BodyTwo,
  createElement,
  HeadlineFour,
  HeadlineThree,
  HeadlineTwo,
  OverlineFive,
  OverlineOne,
  OverlineThree,
} from '@nucleus/ncu-section-builder';
import { Height, InfoMaxWidth, SectionTypeEnum } from '@nucleus/types/web';
import { Alignment } from '@nucleus/types/web/alignment';
import { Position } from '@nucleus/types/web';
import { SectionDefaultBuilder } from '../SectionDefaultBuilder';
import {
  StandardBlockButtons,
  StandardSectionBackground,
  buildPlaceholderMedia,
  buildPresetMedia,
  buildStandardWrapper,
  leaderMediaOptionsBackgroundDefault,
  leaderMediaOptionsInlineDefault,
} from '../helpers';
import { defaultText } from '../text';

const builder = new SectionDefaultBuilder<SectionTypeEnum.Leader>()
  .setWrapper(buildStandardWrapper<SectionTypeEnum.Leader>)
  .addLayout('layout1', ({ lookConfig }) => ({
    ...StandardSectionBackground,
    colorPaletteVariation: 'variation-1',
    height: Height.Medium,
    blockLayoutVariant: 'variant2',
    blockEnabledInfo: { overline: true, headline: true, byline: false, body: true },
    blockInfoPosition: Position.Center,
    blockHeadlineAlignment: Alignment.Left,
    blockHeadlineMaxWidth: InfoMaxWidth.Medium,
    blockBodyAlignment: Alignment.Left,
    blockButtonsAlignment: Alignment.Left,
    ...StandardBlockButtons,
    blocks: [
      {
        overline: <OverlineThree>{defaultText.overline.standard}</OverlineThree>,
        headline: <HeadlineThree>{defaultText.headline.short}</HeadlineThree>,
        body: <BodyTwo>{defaultText.body.standard}</BodyTwo>,
        actions: [],
        mediaItems: [buildPlaceholderMedia({ useImage: true, image: lookConfig?.placeholders?.LightBokeh })],
        itemAlignment: 'left',
        itemBackgroundMedia: buildPresetMedia('listMedia', lookConfig),
        itemEnabledInfo: { mediaItems: true, overline: false, headline: true, body: true, buttons: true },
        itemInfoPosition: 'bottom-left',
        itemMediaDisplayType: 'inline',
        itemMediaOptions: leaderMediaOptionsInlineDefault(lookConfig),
        itemSpacing: 'constrained',
        gridAlignment: 'center',
        gridTypeDesktop: 'swipe',
        gridTypeMobile: 'swipe',
      },
    ],
  }))
  .addLayout('layout2', ({ lookConfig }) => ({
    ...StandardSectionBackground,
    colorPaletteVariation: 'variation-2',
    height: Height.Large,
    blockLayoutVariant: 'variant2',
    blockEnabledInfo: { overline: false, headline: false, byline: false, body: false },
    blockInfoPosition: Position.Center,
    blockHeadlineAlignment: Alignment.Center,
    blockHeadlineMaxWidth: InfoMaxWidth.Medium,
    blockBodyAlignment: Alignment.Center,
    blockButtonsAlignment: Alignment.Center,
    ...StandardBlockButtons,
    blocks: [
      {
        headline: <HeadlineTwo>{defaultText.headline.short}</HeadlineTwo>,
        body: <BodyTwo>{defaultText.body.standard}</BodyTwo>,
        actions: [],
        mediaItems: [buildPlaceholderMedia({ useImage: true, image: lookConfig?.placeholders?.LightBokeh })],
        itemAlignment: 'left',
        itemBackgroundMedia: buildPresetMedia('listMedia', lookConfig),
        itemEnabledInfo: { mediaItems: true, overline: true, headline: true, body: true, buttons: true },
        itemInfoPosition: 'bottom-left',
        itemMediaDisplayType: 'background',
        itemMediaOptions: leaderMediaOptionsBackgroundDefault(lookConfig),
        itemSpacing: 'constrained',
        gridAlignment: 'center',
        gridTypeDesktop: 'standard',
        gridTypeMobile: 'standard',
      },
    ],
  }))
  .addLayout('layout3', ({ lookConfig }) => ({
    ...StandardSectionBackground,
    colorPaletteVariation: 'variation-3',
    height: Height.Medium,
    blockLayoutVariant: 'variant1',
    blockEnabledInfo: { overline: false, headline: false, byline: false, body: false },
    blockInfoPosition: Position.Left,
    blockHeadlineAlignment: Alignment.Left,
    blockHeadlineMaxWidth: InfoMaxWidth.Small,
    blockBodyAlignment: Alignment.Left,
    blockButtonsAlignment: Alignment.Left,
    ...StandardBlockButtons,
    blocks: [
      {
        overline: <OverlineThree>{defaultText.overline.standard}</OverlineThree>,
        headline: <HeadlineThree>{defaultText.headline.short}</HeadlineThree>,
        body: <BodyTwo>{defaultText.body.standard}</BodyTwo>,
        actions: [],
        mediaItems: [
          buildPlaceholderMedia({ useImage: true, image: lookConfig?.placeholders?.LightBokeh }),
          buildPlaceholderMedia({ useImage: true, image: lookConfig?.placeholders?.LightBokeh }),
          buildPlaceholderMedia({ useImage: true, image: lookConfig?.placeholders?.LightBokeh }),
        ],
        itemAlignment: 'left',
        itemBackgroundMedia: buildPresetMedia('listMedia', lookConfig),
        itemEnabledInfo: { mediaItems: true, overline: false, headline: true, body: true, buttons: true },
        itemInfoPosition: 'bottom-left',
        itemMediaDisplayType: 'inline',
        itemMediaOptions: leaderMediaOptionsInlineDefault(lookConfig),
        itemSpacing: 'constrained',
        gridAlignment: 'center',
        gridTypeDesktop: 'standard',
        gridTypeMobile: 'standard',
      },
    ],
  }))
  .addLayout('layout4', ({ lookConfig }) => ({
    ...StandardSectionBackground,
    colorPaletteVariation: 'variation-2',
    height: Height.Medium,
    blockLayoutVariant: 'variant1',
    blockEnabledInfo: { overline: true, headline: true, byline: false, body: false },
    blockInfoPosition: Position.Center,
    blockHeadlineAlignment: Alignment.Center,
    blockHeadlineMaxWidth: InfoMaxWidth.Medium,
    blockBodyAlignment: Alignment.Left,
    blockBodyMaxColumns: 2,
    blockButtonsAlignment: Alignment.Center,
    ...StandardBlockButtons,
    blocks: [
      {
        overline: <OverlineThree>{defaultText.overline.standard}</OverlineThree>,
        headline: <HeadlineThree>{defaultText.headline.short}</HeadlineThree>,
        body: [
          ...BodyTwo({ children: defaultText.body.long }),
          ...BodyTwo({ children: '' }),
          ...BodyTwo({ children: defaultText.body.long2 }),
          ...BodyTwo({ children: '' }),
          ...BodyTwo({ children: defaultText.body.long3 }),
        ],
        actions: [],
        mediaItems: [],
        itemAlignment: 'left',
        itemBackgroundMedia: buildPresetMedia('listMedia', lookConfig),
        itemEnabledInfo: { mediaItems: true, overline: true, headline: true, body: true, buttons: true },
        itemInfoPosition: 'bottom-left',
        itemMediaDisplayType: 'inline',
        itemMediaOptions: leaderMediaOptionsInlineDefault(lookConfig),
        itemSpacing: 'constrained',
        gridAlignment: 'center',
        gridTypeDesktop: 'swipe',
        gridTypeMobile: 'swipe',
      },
    ],
  }))
  .addLayout('layout5', ({ lookConfig }) => ({
    backgroundMedia: { presetMediaKey: 'sectionBackground', presetMediaOrder: 'first' },
    blockBackgroundMedia: {
      useImage: true,
      image: lookConfig?.placeholders?.LightBokeh,
      blending: { opacity: 0.25, blendMode: 'none', fade: 'none' },
      presetMediaKey: 'blockBackground',
      presetMediaOrder: 'first',
    },
    colorPaletteVariation: 'variation-2',
    height: Height.Large,
    blockLayoutVariant: 'variant1',
    blockEnabledInfo: { overline: true, headline: true, byline: false, body: true },
    blockInfoPosition: Position.Center,
    blockHeadlineAlignment: Alignment.Right,
    blockHeadlineMaxWidth: InfoMaxWidth.Medium,
    blockBodyAlignment: Alignment.Left,
    blockButtonsAlignment: Alignment.Left,
    ...StandardBlockButtons,
    blocks: [
      {
        overline: <OverlineOne>{defaultText.overline.standard}</OverlineOne>,
        headline: <HeadlineThree>{defaultText.headline.long}</HeadlineThree>,
        body: <BodyTwo>{defaultText.body.short}</BodyTwo>,
        actions: [],
        mediaItems: [],
        itemAlignment: 'left',
        itemBackgroundMedia: buildPresetMedia('listMedia', lookConfig),
        itemEnabledInfo: { mediaItems: true, overline: true, headline: true, body: true, buttons: true },
        itemInfoPosition: 'bottom-left',
        itemMediaDisplayType: 'background',
        itemMediaOptions: leaderMediaOptionsBackgroundDefault(lookConfig),
        itemSpacing: 'full',
        gridAlignment: 'center',
        gridTypeDesktop: 'standard',
        gridTypeMobile: 'standard',
      },
    ],
  }))
  .addLayout('layout6', ({ lookConfig }) => ({
    ...StandardSectionBackground,
    colorPaletteVariation: 'variation-2',
    height: Height.Medium,
    blockLayoutVariant: 'variant1',
    blockEnabledInfo: { overline: true, headline: true, byline: false, body: true },
    blockInfoPosition: Position.Left,
    blockHeadlineAlignment: Alignment.Left,
    blockHeadlineMaxWidth: InfoMaxWidth.Large,
    blockBodyAlignment: Alignment.Left,
    blockButtonsAlignment: Alignment.Left,
    ...StandardBlockButtons,
    blocks: [
      {
        overline: <OverlineFive>{defaultText.overline.standard}</OverlineFive>,
        headline: <HeadlineFour>{defaultText.headline.short}</HeadlineFour>,
        body: <BodyThree>{defaultText.body.standard}</BodyThree>,
        actions: [],
        mediaItems: [buildPlaceholderMedia({ useImage: true, image: lookConfig?.placeholders?.LightBokeh })],
        itemAlignment: 'left',
        itemBackgroundMedia: buildPresetMedia('listMedia', lookConfig),
        itemEnabledInfo: { mediaItems: true, overline: true, headline: true, body: true, buttons: true },
        itemInfoPosition: 'bottom-left',
        itemMediaDisplayType: 'inline',
        itemMediaOptions: leaderMediaOptionsInlineDefault(lookConfig),
        itemSpacing: 'constrained',
        gridAlignment: 'center',
        gridTypeDesktop: 'standard',
        gridTypeMobile: 'swipe',
      },
    ],
  }));

export default builder.build();
